import React, { useEffect, useState } from "react";

import i18n from "i18next";
import LanguageDetector from "i18next-browser-languagedetector";
import translationDE from "../../../locale/de/translation.json";
import translationEN from "../../../locale/en/translation.json";
import { IDokument } from "../../../Models/IDokument";
import { IDokument2Dokumentgruppe } from "../../../Models/IDokument2Dokumentegruppe";
import { IDokumentSprache } from "../../../Models/IDokumentSprache";
import { helperMethods } from "../../../helpers/helperMethods";
import { fetchWrapper } from "../../../helpers/fetchwrapper";
import { baseUrl } from "../../../helpers/constants";
import ExecuteDialog from "../../../Controls/ExecuteDialog";
import { IFetchWrapperResultDto } from "../../../Models/IFetchWrapperResultDto";
import {
  Link,
  createTableColumn,
  Text,
  Tooltip,
  Button,
  Field,
  TableCellLayout,
} from "@fluentui/react-components";
import {
  DeleteArrowBackRegular,
  DeleteRegular,
  DocumentCube24Regular,
  DocumentPdf24Regular,
} from "@fluentui/react-icons";
import { StackShim } from "@fluentui/react-migration-v8-v9";

const resources = {
  en: {
    translation: translationEN,
  },
  de: {
    translation: translationDE,
  },
};

// initialize i18next with catalog and language to use
i18n.use(LanguageDetector).init({
  resources,
});

interface IDocumentColumnsProps {
  onReloadDocuments: any;
}

export function DocumentColumns(
  searchParam: string[],
  props: IDocumentColumnsProps,
) {
  const [showDownloadNotification, setShowDownloadNotification] =
    useState(false);

  const [roles, setRoles] = useState([""]);
  const [deleteSelectedGuid, setDeleteSelectedGuid] = useState<
    string | undefined
  >(undefined);
  const [changeDeleteSelectedGuid, setChangeDeleteSelectedGuid] = useState<
    string | undefined
  >(undefined);
  const [fetchWrapperResult, setFetchWrapperResult] = useState<
    IFetchWrapperResultDto | undefined
  >(undefined);

  const GetUserRoll = () => {
    fetchWrapper
      .getWithAuth(`${baseUrl}/api/account/getmyroles`)
      .then((result) => {
        setRoles(result);
      })
      .catch((error) => {});
  };

  useEffect(() => {
    GetUserRoll();
  }, []);

  const deleteExecuteDialog = () => {
    if (deleteSelectedGuid != undefined) {
      return (
        <ExecuteDialog
          isDialogOpen={deleteSelectedGuid != undefined}
          title={i18n.t<string>("deleteDocumentQuestion")}
          subText={""}
          primaryButtonIconName={"Delete"}
          primaryButtonText={i18n.t<string>("delete")}
          executeUrl={`${baseUrl}/api/Documents/DeleteDocument?dokumentGuid=${deleteSelectedGuid}`}
          onDismiss={() => setDeleteSelectedGuid(undefined)}
          onExecute={(fetchwrapperResult: any) => {
            setFetchWrapperResult(fetchwrapperResult);
            setDeleteSelectedGuid(undefined);
            props.onReloadDocuments();
          }}
          onError={() => setDeleteSelectedGuid(undefined)}
        />
      );
    }
  };

  const changeDeleteExecuteDialog = () => {
    if (changeDeleteSelectedGuid != undefined) {
      return (
        <ExecuteDialog
          isDialogOpen={changeDeleteSelectedGuid != undefined}
          title={i18n.t<string>("restoreDocumentQuestion")}
          subText={""}
          primaryButtonIconName={"Refresh"}
          primaryButtonText={i18n.t<string>("restore")}
          executeUrl={`${baseUrl}/api/Documents/ChangeDeletedDocument?dokumentGuid=${changeDeleteSelectedGuid}`}
          onDismiss={() => setChangeDeleteSelectedGuid(undefined)}
          onExecute={(fetchwrapperResult: any) => {
            setFetchWrapperResult(fetchwrapperResult);
            setChangeDeleteSelectedGuid(undefined);
            props.onReloadDocuments();
          }}
          onError={() => setChangeDeleteSelectedGuid(undefined)}
        />
      );
    }
  };
  const getHighlightedText = (text: string, highlight: string[]) => {
    if (!highlight || highlight.length === 0) return <span>{text}</span>;

    // Baue einen regulären Ausdruck aus dem Array
    const escaped = highlight.map(
      (h) => h.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"), // Escape für Regex
    );
    const regex = new RegExp(`(${escaped.join("|")})`, "gi");

    const parts = text.split(regex);

    return (
      <span>
        {parts.map((part, i) => (
          <span
            key={i}
            style={
              highlight.some((h) => h.toLowerCase() === part.toLowerCase())
                ? { background: "#FFF01F" }
                : {}
            }
          >
            {part}
          </span>
        ))}
      </span>
    );
  };

  const columns = [
    // ------------Extension Bild------------
    createTableColumn<IDokument>({
      columnId: "docFileExtension",
      compare: (a, b) => {
        if (a.docFileExtension && b.docFileExtension) {
          return a.docFileExtension.localeCompare(b.docFileExtension);
        } else return 0;
      },
      renderHeaderCell: () => {
        return <Text> {i18n.t<string>("docFileExtension")}</Text>;
      },
      renderCell: (item) => {
        return (
          <div>
            <Tooltip content={item.docFileExtension} relationship={"label"}>
              <div>
                {item.docFileExtension == ".PDF" && <DocumentPdf24Regular />}
                {item.docFileExtension == ".STP" && <DocumentCube24Regular />}
              </div>
            </Tooltip>
          </div>
        );
      },
    }),

    // ------------Dateiname------------
    createTableColumn<IDokument>({
      columnId: "docFileName",
      renderHeaderCell: () => {
        return <Text> {i18n.t<string>("FileName")}</Text>;
      },
      compare: (a, b) => {
        if (a.docFileName && b.docFileName) {
          return a.docFileName.localeCompare(b.docFileName);
        } else return 0;
      },
      renderCell: (item) => {
        return (
          <TableCellLayout truncate>
            <Link
              style={{
                color: item.isDeleted == true ? "black" : "",
                textDecoration: item.isDeleted == true ? "line-through" : "",
              }}
              onClick={(event: any) => {
                setShowDownloadNotification(true);
                helperMethods.downloadFile(item, true).finally(() => {
                  setTimeout(() => {
                    setShowDownloadNotification(false);
                  }, 1500);
                });
              }}
            >
              {getHighlightedText(item.docFileName, searchParam)}
            </Link>
          </TableCellLayout>
        );
      },
    }),

    // ------------Dokumentengruppe------------
    createTableColumn<IDokument>({
      columnId: "docGroupId",
      renderHeaderCell: () => {
        return <Text> {i18n.t<string>("docGroup")}</Text>;
      },
      renderCell: (item) => {
        return (
          <TableCellLayout truncate>
            <StackShim>
              {item.dokument2Dokumentgruppen.map(
                (gruppe: IDokument2Dokumentgruppe) => {
                  return <Text>{gruppe.dokumentgruppe.nameDE}</Text>;
                },
              )}
            </StackShim>
          </TableCellLayout>
        );
      },
    }),

    // ------------Sprachen------------
    createTableColumn<IDokument>({
      columnId: "sprachenId",

      renderHeaderCell: () => {
        return <Text> {i18n.t<string>("languages")}</Text>;
      },
      renderCell: (item) => {
        return (
          <TableCellLayout truncate>
            <StackShim>
              {item.dokumentSprachen.map((sprache: IDokumentSprache) => {
                return <Text>{sprache.sprache.nameDE}</Text>;
              })}
            </StackShim>
          </TableCellLayout>
        );
      },
    }),

    // ------------artikelId------------
    createTableColumn<IDokument>({
      columnId: "artikelId",
      compare: (a, b) => {
        if (a.artikelId && b.artikelId) {
          return a.artikelId.localeCompare(b.artikelId);
        } else return 0;
      },
      renderHeaderCell: () => {
        return <Text> {i18n.t<string>("artikelId")}</Text>;
      },
      renderCell: (item) => {
        return (
          <TableCellLayout truncate>
            <Text>
              {getHighlightedText(
                item.artikelId ? item.artikelId : "",
                searchParam,
              )}
            </Text>
          </TableCellLayout>
        );
      },
    }),

    // ------------Auftrag------------
    createTableColumn<IDokument>({
      columnId: "auftragId",
      compare: (a, b) => {
        if (a.auftragId && b.auftragId) {
          return a.auftragId.localeCompare(b.auftragId);
        } else return 0;
      },
      renderHeaderCell: () => {
        return <Text> {i18n.t<string>("auftrag")}</Text>;
      },
      renderCell: (item) => {
        return (
          <TableCellLayout truncate>
            <Text>
              {getHighlightedText(
                item.auftragId ? item.auftragId : "",
                searchParam,
              )}
            </Text>
          </TableCellLayout>
        );
      },
    }),

    // ------------System------------
    createTableColumn<IDokument>({
      columnId: "produktId",
      compare: (a, b) => {
        if (a.produktId && b.produktId) {
          return a.produktId.localeCompare(b.produktId);
        } else return 0;
      },
      renderHeaderCell: () => {
        return <Text> {i18n.t<string>("systemId")}</Text>;
      },
      renderCell: (item) => {
        return (
          <TableCellLayout truncate>
            <Text>
              {getHighlightedText(
                item.produktId ? item.produktId : "",
                searchParam,
              )}
            </Text>
          </TableCellLayout>
        );
      },
    }),

    // ------------Erstellt------------
    createTableColumn<IDokument>({
      columnId: "created",
      compare: (a, b) => {
        if (a.created && b.created) {
          return a.created.localeCompare(b.created);
        } else return 0;
      },
      renderHeaderCell: () => {
        return <Text> {i18n.t<string>("created")}</Text>;
      },
      renderCell: (item) => {
        return (
          <TableCellLayout truncate>
            <Text>
              {new Date(
                item.created != null ? item.created : "",
              ).toLocaleDateString()}
            </Text>
          </TableCellLayout>
        );
      },
    }),

    // ------------Geändert------------
    createTableColumn<IDokument>({
      columnId: "changeDate",
      compare: (a, b) => {
        if (a.changeDate && b.changeDate) {
          return a.changeDate.localeCompare(b.changeDate);
        } else return 0;
      },
      renderHeaderCell: () => {
        return <Text> {i18n.t<string>("changeDate")}</Text>;
      },
      renderCell: (item) => {
        return (
          <TableCellLayout truncate>
            <Text>
              {item.changeDate
                ? new Date(item.changeDate).toLocaleDateString()
                : ""}
            </Text>
          </TableCellLayout>
        );
      },
    }),
  ];

  if (roles.includes("Vertriebsadmin")) {
    columns.push(
      // ------------Delete Button------------
      createTableColumn<IDokument>({
        columnId: "delBtnId",
        compare: (a, b) => {
          if (a.dokumentGuid && b.dokumentGuid) {
            return a.dokumentGuid.localeCompare(b.dokumentGuid);
          } else return 0;
        },
        renderCell: (item) => {
          if (item.isDeleted == true) {
            return (
              <Tooltip
                content={i18n.t<string>("restoreDocument")}
                relationship={"label"}
              >
                <Button
                  appearance="transparent"
                  icon={<DeleteArrowBackRegular />}
                  onClick={() => {
                    setChangeDeleteSelectedGuid(item.dokumentGuid);
                  }}
                />
              </Tooltip>
            );
          } else {
            return (
              <Tooltip
                relationship="label"
                content={i18n.t<string>("deleteDocument")}
              >
                <Button
                  appearance="transparent"
                  onClick={() => {
                    setDeleteSelectedGuid(item.dokumentGuid);
                  }}
                  icon={<DeleteRegular />}
                ></Button>
              </Tooltip>
            );
          }
        },
      }),
    );
  }

  return {
    changeDeleteExecuteDialog: () => {
      return changeDeleteExecuteDialog();
    },
    deleteExecuteDialog: () => {
      return deleteExecuteDialog();
    },
    showDownloadNotification: showDownloadNotification,
    getColumns: () => {
      return columns;
    },
  };
}
